import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import Modal, { Field, TextInput, Select, PrimaryButton, SecondaryButton } from "@/components/FormKit";
import { toast } from "sonner";
import { fmtCurrency, fmtDate } from "@/lib/format";

export default function Ownerships() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", unit_id: "", purchase_date: "", total_price: "" });

  const load = async () => {
    const [o, c, u] = await Promise.all([
      api.get("/company/ownerships"),
      api.get("/company/customers"),
      api.get("/company/units"),
    ]);
    setItems(o.data);
    setCustomers(c.data);
    setUnits(u.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/company/ownerships", {
        ...form,
        total_price: form.total_price ? Number(form.total_price) : null,
      });
      toast.success("Ownership assigned");
      setOpen(false);
      setForm({ customer_id: "", unit_id: "", purchase_date: "", total_price: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="company-ownerships-page">
      <PageHeader
        eyebrow="Mapping"
        title="Unit ownerships"
        description="Which customer owns which unit, with purchase details."
        actions={<PrimaryButton onClick={() => setOpen(true)} testId="new-ownership-button">+ Assign Unit</PrimaryButton>}
      />

      <DataTable
        testId="ownerships-table"
        columns={[
          { key: "customer", label: "Customer", render: (r) => <div className="font-bold">{r.customer?.name || "—"}</div> },
          { key: "unit", label: "Unit", render: (r) => <span className="font-mono">{r.unit?.unit_no || "—"}</span> },
          { key: "project", label: "Project", render: (r) => r.project?.name || "—" },
          { key: "purchase_date", label: "Purchase Date", render: (r) => fmtDate(r.purchase_date) },
          { key: "total_price", label: "Price", render: (r) => fmtCurrency(r.total_price) },
        ]}
        rows={items}
        empty="No ownerships yet."
      />

      <Modal open={open} onClose={() => setOpen(false)} title="Assign unit to customer">
        <form onSubmit={submit}>
          <Field label="Customer">
            <Select testId="form-own-customer" required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Unit">
            <Select testId="form-own-unit" required value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })}>
              <option value="">Select unit…</option>
              {units.filter((u) => u.status !== "sold").map((u) => (
                <option key={u.id} value={u.id}>{u.project?.name} · {u.tower?.name} · {u.unit_no}</option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Purchase date"><TextInput testId="form-own-date" type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></Field>
            <Field label="Total price"><TextInput testId="form-own-price" type="number" step="0.01" value={form.total_price} onChange={(e) => setForm({ ...form, total_price: e.target.value })} /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" testId="submit-ownership">Assign</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
